import { mapDeep } from './map-deep'

export type SortedList = {
  /**
   * The field the list hangs under, as the cached response names it —
   * `options`, `imageRules`, `pictures`. Several where one list is reached by
   * more than one name: a group's children are `groups` at the root and
   * `children` below it. Left out where the response *is* the list.
   *
   * Named rather than found: a detail response carries `imageRules`,
   * `properties` and `products` side by side, all keyed by `id` and all
   * ordered by `sortOrder`, so an id set matches more than one of them and a
   * drag on one would permute the others.
   */
  listKey?: string | readonly string[]
  /** What names a row, in the payload and in the cached list. */
  idKey?: string
  /** The field the list is drawn in the order of, where the response carries
   *  one. The rows are moved into place either way. */
  orderKey?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * A cached response with one list put in the order a sort payload asks for —
 * what a surface draws while the write is still on its way.
 *
 * Both halves of "in order": the rows the payload names take each other's
 * places, and the field they are ordered by is written where the response
 * carries one. A projection that left that column out is drawn from the
 * array's own order and is served by the first half alone.
 *
 * **A row the payload does not name keeps its place.** A response holds more
 * than one write is about — the formula library carries every formula while
 * the summary card orders a handful, the classification list carries four
 * kinds in one array — and those rows are not this write's to move.
 *
 * A cache that does not hold the list comes back untouched, and so does one
 * that holds it twice at the same depth: a measure's own pictures and the
 * pictures of one of its image rules are both `images`, and a write that
 * cannot tell two candidates apart writes neither. The refetch behind it
 * settles both cases.
 */
export function applySortOrder<T>(
  cached: T,
  /** One row per place in the list: which row, and where it goes. */
  items: readonly unknown[],
  { listKey, idKey = 'id', orderKey = 'sortOrder' }: SortedList,
): T {
  const names =
    listKey === undefined
      ? []
      : typeof listKey === 'string'
        ? [listKey]
        : listKey

  const places = new Map<unknown, number>()
  for (const [index, item] of items.entries()) {
    if (!isRecord(item)) continue
    const order = item[orderKey]
    places.set(item[idKey], typeof order === 'number' ? order : index)
  }
  if (places.size === 0) return cached

  const idOf = (row: unknown) => (isRecord(row) ? row[idKey] : undefined)
  const isNamed = (row: unknown) => places.has(idOf(row))

  /** A list the write is about holds every row the payload names. It may hold
   *  more; those rows stay where they are. */
  const holdsThePayload = (node: readonly unknown[]) => {
    if (node.length < places.size) return false
    let found = 0
    for (const row of node) if (isNamed(row)) found += 1
    return found === places.size
  }

  // Found first and rewritten after: a response can hold two lists of one name
  // at different depths, and the shallower is the one a write about that
  // response means. Two at the same depth are a tie nothing here can break.
  const candidates: { depth: number; node: readonly unknown[] }[] = []
  const look = (node: unknown, key: string | null, depth: number) => {
    if (Array.isArray(node)) {
      if ((key === null || names.includes(key)) && holdsThePayload(node)) {
        candidates.push({ depth, node })
      }
      for (const row of node) look(row, key, depth)
      return
    }
    if (!isRecord(node)) return
    for (const field in node) look(node[field], field, depth + 1)
  }
  look(cached, null, 0)

  const shallowest = Math.min(...candidates.map(one => one.depth))
  const found = candidates.filter(one => one.depth === shallowest)
  if (found.length !== 1) return cached
  const list = found[0]!.node

  /** The named rows in their new order, ready to take each other's slots. */
  const moved = list
    .filter(isNamed)
    .sort((a, b) => places.get(idOf(a))! - places.get(idOf(b))!)
    .map(row =>
      isRecord(row) && orderKey in row
        ? { ...row, [orderKey]: places.get(idOf(row)) }
        : row,
    )

  let taken = 0
  const reordered = list.map(row => (isNamed(row) ? moved[taken++]! : row))

  return mapDeep(cached, node => (node === list ? reordered : undefined))
}
